import { useEffect } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { useGate } from "@nestoca/gate-react";
import { useTenant } from "@nestoca/multi-tenant";
import { useGetAssignedRepresentative } from "@shared/api/hooks/applications";
import { useTranslation } from "react-i18next";

import { useBusinessHours } from "../use-business-hours";

import {
  getAdvisorContact,
  getExternalBrokerContact,
  getGenericContact,
} from "./support-contact-items";

import type { SupportInfoType } from "./support-contact-items";

export type {
  SupportContactId,
  SupportInfoType,
} from "./support-contact-items";

type UseSupportContactResult = {
  supportContactInfo: SupportInfoType[];
  hasAssignedAdvisor: boolean;
  contactLabel: string;
  isLoading: boolean;
};

export const useSupportContact = ({
  applicationId,
}: {
  applicationId: number;
}): UseSupportContactResult => {
  const { t, i18n } = useTranslation("common");
  const { isBusinessTime } = useBusinessHours();
  const tenant = useTenant();
  const gate = useGate();
  const {
    data: assignedAdvisor,
    isLoading,
    error,
  } = useGetAssignedRepresentative(applicationId);

  // a failed fetch deliberately falls back to the generic contact
  useEffect(() => {
    if (error) {
      datadogRum.addError(error, { applicationId });
    }
  }, [error, applicationId]);

  const advisorContact = assignedAdvisor?.id
    ? getAdvisorContact(assignedAdvisor, t)
    : [];
  const hasAssignedAdvisor = advisorContact.length > 0;

  // only one contact is ever shown: advisor, then underwriter, then generic
  const selectContact = (): SupportInfoType[] => {
    if (hasAssignedAdvisor) {
      return advisorContact;
    }

    if (gate.getRole() === "externalbroker") {
      return getExternalBrokerContact(t);
    }

    return getGenericContact({
      tenant,
      isFrench: i18n.language === "fr",
      isBusinessTime,
      t,
    });
  };

  const contactLabel = hasAssignedAdvisor
    ? t("contact.label.advisor")
    : t("contact.label.generic");

  return {
    supportContactInfo: selectContact(),
    hasAssignedAdvisor,
    contactLabel,
    isLoading,
  };
};
